class PredictiveSearch extends SearchForm {
  static _scrollY = 0;

  constructor() {
    super();
    this.cachedResults = {};
    this.header = document.querySelector('.section-header');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.recentStorageKey = 'rs-recent-searches';
    this.recentLimit = 8;

    this.iconSearch = this.querySelector('.header-icon-search');
    this.iconSearchClose = this.querySelector('.header-icon-search-close');
    this.slide = this.closest('[data-header-search-slide]');
    this.trigger = this.slide ? document.querySelector('[data-header-search-trigger]') : null;
    this.closeButton = this.querySelector('[data-header-search-close]');
    this.submitButton = this.querySelector('[data-search-submit]');
    this.resultsCount = this.querySelector('[data-header-search-count]');
    this.sidebarTarget = this.querySelector('[data-ps-sidebar-target]');
    this.recentRoot = this.querySelector('[data-recent-searches]');
    this.idleResultsMarkup = this.predictiveSearchResults ? this.predictiveSearchResults.innerHTML : '';
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onResultsClick = this.onResultsClick.bind(this);

    if (this.slide) this.slide.inert = true;

    this.setupEventListeners();
    this.renderRecentSearches();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onResultsClick);

    if (this.iconSearch) {
      this.iconSearch.addEventListener('click', this.onToggleSearch.bind(this));
    }
    if (this.iconSearchClose) {
      this.iconSearchClose.addEventListener('click', this.onToggleSearch.bind(this));
    }
    if (this.trigger) {
      this.trigger.addEventListener('click', this.onToggleSearch.bind(this));
    }
    if (this.closeButton) {
      this.closeButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeSearch();
        this.trigger?.focus();
      });
    }
    if (this.submitButton) {
      this.submitButton.addEventListener('click', this.onSearchSubmitClick.bind(this));
    }
    this.input?.addEventListener('input', () => this.fitSearchInput());
    this.fitSearchInput();
  }

  onSearchSubmitClick(event) {
    event.preventDefault();
    if (!this.getQuery()) return;
    this.querySelectorAll('[aria-selected="true"]').forEach((el) => {
      el.setAttribute('aria-selected', 'false');
    });
    this.input.form.requestSubmit();
  }

  fitSearchInput() {
    if (!this.input) return;
    if (typeof CSS !== 'undefined' && CSS.supports?.('field-sizing', 'content')) return;
    this.input.style.width = '0px';
    this.input.style.width = `${Math.max(this.input.scrollWidth, 1)}px`;
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.clearOverlayResults();
      this.close(true);
      this.updateResultsCount(0);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onToggleSearch(event) {
    event?.preventDefault();

    if (this.header.classList.contains('search-open')) {
      this.closeSearch();
      return;
    }

    const pageSearchInput = this.getPageSearchInput();
    if (pageSearchInput) {
      pageSearchInput.focus();
      return;
    }

    this.openSearch();
  }

  getPageSearchInput() {
    if (!document.body.classList.contains('template-search')) return null;
    return document.getElementById('Search-In-Page');
  }

  lockPageScroll() {
    const html = document.documentElement;
    if (html.classList.contains('header-search-open')) return;

    PredictiveSearch._scrollY = window.scrollY;
    document.body.style.setProperty('--scroll-lock-top', `-${PredictiveSearch._scrollY}px`);
    html.classList.add('header-search-open');
  }

  unlockPageScroll() {
    const html = document.documentElement;
    if (!html.classList.contains('header-search-open')) return;

    html.classList.remove('header-search-open');
    if (html.classList.contains('header-menu-scroll-lock')) return;

    document.body.style.removeProperty('--scroll-lock-top');
    window.scrollTo(0, PredictiveSearch._scrollY);
  }

  openSearch() {
    this.header.classList.add('search-open');
    this.classList.add('open');
    document.querySelectorAll('header-menu').forEach((menu) => {
      if (menu.mainDetailsToggle?.hasAttribute('open')) menu.close();
    });
    this.lockPageScroll();
    if (this.slide) {
      this.slide.classList.add('is-open');
      this.slide.setAttribute('aria-hidden', 'false');
      this.slide.inert = false;
    }
    this.trigger?.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.onDocumentKeydown);
    this.renderRecentSearches();
    this.input.focus();
  }

  closeSearch() {
    this.header.classList.remove('search-open');
    this.classList.remove('open');
    this.unlockPageScroll();
    if (this.slide) {
      this.slide.classList.remove('is-open');
      this.slide.setAttribute('aria-hidden', 'true');
      this.slide.inert = true;
    }
    this.trigger?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.onDocumentKeydown);
    this.close(true);
    this.clearOverlayResults();
    this.updateResultsCount(0);
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape') {
      this.closeSearch();
      this.trigger?.focus();
    }
  }

  onFormSubmit(event) {
    const query = this.getQuery();
    if (!query.length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    if (query.length) this.saveRecentSearch(query);
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.clearOverlayResults();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (this.slide?.classList.contains('is-open')) return;

    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onResultsClick(event) {
    const recentButton = event.target.closest('[data-recent-term]');
    if (recentButton) {
      event.preventDefault();
      const term = recentButton.getAttribute('data-recent-term') || '';
      this.input.value = term;
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.input.focus();
      return;
    }

    const addButton = event.target.closest('[data-header-search-add]');
    if (addButton) {
      event.preventDefault();
      this.addProductFromSearch(addButton);
      return;
    }

    const resultLink = event.target.closest('a[href]');
    if (resultLink && this.getQuery()) {
      this.saveRecentSearch(this.getQuery());
    }
  }

  addProductFromSearch(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    const variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const config = fetchConfig('javascript');
    const formData = new FormData();

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');

    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    formData.append('id', variantId);
    formData.append('quantity', '1');

    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }

    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'header-search',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          return;
        }

        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'header-search',
          productVariantId: variantId,
          cartData: response,
        });
        cart.renderContents(response);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      });
  }

  getRecentSearches() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.recentStorageKey));
      return Array.isArray(stored) ? stored.filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  }

  saveRecentSearch(term) {
    const value = (term || '').trim();
    if (!value) return;

    const next = [
      value,
      ...this.getRecentSearches().filter((item) => item.toLowerCase() !== value.toLowerCase()),
    ].slice(0, this.recentLimit);

    localStorage.setItem(this.recentStorageKey, JSON.stringify(next));
    this.renderRecentSearches();
  }

  renderRecentSearches() {
    if (!this.recentRoot) return;

    const list = this.recentRoot.querySelector('[data-recent-searches-list]');
    const items = this.getRecentSearches();

    if (!list || !items.length) {
      this.recentRoot.hidden = true;
      if (list) list.innerHTML = '';
      return;
    }

    this.recentRoot.hidden = false;
    list.innerHTML = items
      .map((term) => {
        const safe = this.escapeHtml(term);
        return `<li><button type="button" class="header-search-pill" data-recent-term="${safe}">${safe}</button></li>`;
      })
      .join('');
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML.replace(/"/g, '&quot;');
  }

  clearOverlayResults() {
    if (this.sidebarTarget) this.sidebarTarget.innerHTML = '';
    if (this.slide && this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = this.idleResultsMarkup;
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('[role="option"]')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selected = this.querySelector('[aria-selected="true"]');
    if (!selected) return;

    const selectedOption = selected.matches('a, button') ? selected : selected.querySelector('a, button');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = `${this.slide ? 'header' : 'page'}:${searchTerm.replace(' ', '-').toLowerCase()}`;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const params = new URLSearchParams({
      q: searchTerm,
      section_id: this.slide ? 'header-predictive-search' : 'predictive-search',
    });

    if (this.slide) {
      params.set('resources[type]', 'product,query');
      params.set('resources[limit]', '8');
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
    }

    fetch(`${routes.predictive_search_url}?${params.toString()}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search, #shopify-section-header-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          if (Boolean(predictiveSearchInstance.slide) === Boolean(this.slide)) {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    const parsed = new DOMParser().parseFromString(resultsMarkup, 'text/html');
    const sidebar = parsed.querySelector('[data-ps-sidebar]');
    const main = parsed.querySelector('[data-ps-main]');

    if (this.slide && this.sidebarTarget && sidebar && main) {
      this.sidebarTarget.innerHTML = sidebar.innerHTML;
      this.predictiveSearchResults.innerHTML = main.innerHTML;
    } else if (this.slide) {
      this.clearOverlayResults();
      if (resultsMarkup) this.predictiveSearchResults.innerHTML = resultsMarkup;
    } else {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
    }

    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
    this.renderRecentSearches();
    const countEl = this.querySelector('[data-header-search-results-count]');
    this.updateResultsCount(countEl ? parseInt(countEl.textContent, 10) : 0);
  }

  updateResultsCount(count) {
    if (!this.resultsCount) return;

    if (!count) {
      this.resultsCount.hidden = true;
      this.resultsCount.textContent = '';
      return;
    }

    this.resultsCount.hidden = false;
    this.resultsCount.textContent = `${String(count).padStart(2, '0')} RESULTS`;
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) this.setLiveRegionText(liveRegion.textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.slide) {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults?.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
